import { faSearch } from "@fortawesome/free-solid-svg-icons"
import { ArrowRight, MapPin, Store } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import InputField from "../../components/InputField/InputField"
import { usePublicStoreGroup } from "../../hooks/StoreGroups/usePublicStoreGroup"
import type { StoreGroup } from "../../Objects/StoreGroups/StoreGroup"
import { SEO } from "../../seo/Seo"
import { calculateDistanceInMeters } from "../../utils/geo"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"

const publicGroupAnimationDuration = "1.6s"

const resolvePosition = async (): Promise<{ latitude: number; longitude: number } | null> => {
  if (!navigator.geolocation) {
    return null
  }

  return await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      () => resolve(null),
      { maximumAge: 300000, timeout: 7000, enableHighAccuracy: false },
    )
  })
}

export const StoreGroupPublicPage = () => {
  const { groupSlug } = useParams<{ groupSlug: string }>()

  const { t } = useTranslation("publicMenu")

  const navigate = useNavigate()

  const { handleGetPublicStoreGroup } = usePublicStoreGroup()

  const [group, setGroup] = useState<StoreGroup | null>(null)

  const [loading, setLoading] = useState(true)

  const [error, setError] = useState("")

  const [search, setSearch] = useState("")

  const [position, setPosition] = useState<{ latitude: number; longitude: number } | null>(null)

  useEffect(() => {
    let active = true

    const load = async () => {
      if (!groupSlug) {
        return
      }

      setLoading(true)
      setError("")

      try {
        const response = await handleGetPublicStoreGroup(groupSlug)

        if (!active) {
          return
        }

        if (response.group) {
          setGroup(response.group)
          return
        }

        setError(response.error)
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : t("store_group.load_error"))
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      active = false
    }
  }, [groupSlug, handleGetPublicStoreGroup, t])

  useEffect(() => {
    resolvePosition().then(setPosition)
  }, [])

  const stores = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()

    return (group?.stores ?? [])
      .filter((store) => {
        if (!normalizedSearch) {
          return true
        }

        return `${store.companyName} ${store.companyMessage ?? ""} ${store.addressLine ?? ""}`
          .toLowerCase()
          .includes(normalizedSearch)
      })
      .map((store) => {
        const distance =
          position && typeof store.latitude === "number" && typeof store.longitude === "number"
            ? calculateDistanceInMeters(position.latitude, position.longitude, store.latitude, store.longitude)
            : null

        return { ...store, distance }
      })
      .sort((a, b) => {
        if (a.distance !== null && b.distance !== null) {
          return a.distance - b.distance
        }

        if (a.distance !== null) {
          return -1
        }

        if (b.distance !== null) {
          return 1
        }

        return a.companyName.localeCompare(b.companyName)
      })
  }, [group?.stores, position, search])

  const formatDistance = (distance: number | null) => {
    if (distance === null) {
      return null
    }

    return distance < 1000
      ? t("store_group.distance_meters", { value: Math.round(distance) })
      : t("store_group.distance_km", { value: (distance / 1000).toFixed(1) })
  }

  return (
    <div className={`${publicAnimatedBackgroundShellClassName} min-h-screen bg-white text-slate-900`}>
      <PublicAnimatedBackground baseColor="#ffffff" />

      <SEO
        title={group ? t("store_group.seo_title_with_name", { name: group.name }) : t("store_group.seo_title")}
        description={t("store_group.seo_description")}
        path={`/g/${groupSlug ?? ""}`}
        siteName={group?.name ?? "catalojei"}
      />

      <main className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-4 py-5 sm:px-6">
        <Animate type={AnimationTypes.fadeInTraslate} duration={publicGroupAnimationDuration}>
          <header className="mb-5 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-black uppercase text-orange-700">
                <Store size={14} />

                {t("store_group.badge")}
              </div>

              <div className="flex items-center gap-3">
                {group?.iconUrl && (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white">
                    <img
                      src={group.iconUrl.toClientRequest()}
                      alt={group.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}

                <div>
                  <h1 className="text-2xl font-black text-slate-950 sm:text-3xl">
                    {group?.name ?? t("store_group.loading_title")}
                  </h1>

                  <p className="mt-1 max-w-2xl text-sm font-medium text-justify text-slate-600">
                    {group?.description || t("store_group.description")}
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full sm:max-w-xs">
              <InputField
                type="text"
                value={search}
                placeholder={t("store_group.search_placeholder")}
                icon={faSearch}
                maxLength={100}
                animationDuration={publicGroupAnimationDuration}
                animationTypes={AnimationTypes.fadeInTraslate}
                onChange={(event) => setSearch(event.target.value)}
                descriptionKey="interactionDescriptions:pages.storeGroupPublic.storeGroupPublicPage.input.searchPlaceholder"
              />
            </div>
          </header>
        </Animate>

        {loading ? (
          <Animate type={AnimationTypes.fadeInTraslate} duration={publicGroupAnimationDuration}>
            <div className="grid gap-3">
              {[0, 1, 2].map((item) => (
                <div key={item} className="h-28 animate-pulse rounded-lg border border-slate-200 bg-white" />
              ))}
            </div>
          </Animate>
        ) : error ? (
          <Animate type={AnimationTypes.fadeInTraslate} duration={publicGroupAnimationDuration}>
            <section className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm font-bold text-red-700">
              {error}
            </section>
          </Animate>
        ) : stores.length === 0 ? (
          <Animate type={AnimationTypes.fadeInTraslate} duration={publicGroupAnimationDuration}>
            <section className="rounded-lg border border-slate-200 bg-white p-5 text-sm font-bold text-slate-500">
              {t("store_group.empty")}
            </section>
          </Animate>
        ) : (
          <Animate type={AnimationTypes.fadeInTraslate} duration={publicGroupAnimationDuration}>
            <section className="grid gap-3">
              {stores.map((store) => {
                const distance = formatDistance(store.distance)

                return (
                  <Animate
                    key={store.memberId}
                    type={AnimationTypes.fadeInTraslate}
                    duration={publicGroupAnimationDuration}
                  >
                    <article className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                      <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                        <div className="min-w-0">
                          <div className="flex items-start gap-3">
                            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                              {store.iconUrl ? (
                                <img
                                  src={store.iconUrl.toClientRequest()}
                                  alt={store.companyName}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <Store className="h-7 w-7 text-slate-400" />
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex min-w-0 flex-wrap items-center gap-2">
                                <h2 className="min-w-0 flex-1 truncate text-base font-black text-slate-950">
                                  {store.companyName}
                                </h2>

                                <span
                                  className={`rounded-md border px-2 py-1 text-[10px] font-black uppercase sm:hidden ${
                                    store.isOpenNow
                                      ? "border-emerald-300 bg-emerald-100 text-emerald-800"
                                      : "border-red-200 bg-red-50 text-red-700"
                                  }`}
                                >
                                  {store.isOpenNow ? t("store_group.open") : t("store_group.closed")}
                                </span>
                              </div>

                              {store.addressLine && (
                                <span className="mt-1 inline-flex min-w-0 items-start gap-1 text-xs font-bold text-slate-500">
                                  <MapPin size={13} className="mt-0.5 shrink-0" />
                                  <span className="line-clamp-2">{store.addressLine}</span>
                                </span>
                              )}
                            </div>
                          </div>

                          {store.companyMessage && (
                            <p className="mt-3 text-justify text-sm font-medium leading-relaxed text-slate-600">
                              {store.companyMessage}
                            </p>
                          )}

                          {distance && (
                            <p className="mt-2 text-right text-xs font-black text-orange-700 sm:hidden">{distance}</p>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 sm:min-w-[170px] sm:items-end">
                          <span
                            className={`hidden rounded-md border px-2 py-1 text-[10px] font-black uppercase sm:inline-flex ${
                              store.isOpenNow
                                ? "border-emerald-300 bg-emerald-100 text-emerald-800"
                                : "border-red-200 bg-red-50 text-red-700"
                            }`}
                          >
                            {store.isOpenNow ? t("store_group.open") : t("store_group.closed")}
                          </span>

                          {distance && (
                            <span className="hidden text-xs font-black text-orange-700 sm:block">{distance}</span>
                          )}

                          <button
                            type="button"
                            onClick={() => navigate(`/${store.slug}`)}
                            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border-2 border-orange-700 bg-orange-600 px-4 text-sm font-black text-white shadow-sm transition hover:bg-orange-700 sm:w-auto"
                            data-interaction-description-key="interactionDescriptions:pages.storeGroupPublic.storeGroupPublicPage.button.navigate"
                          >
                            {t("store_group.open_menu")}
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      </div>
                    </article>
                  </Animate>
                )
              })}
            </section>
          </Animate>
        )}
      </main>
    </div>
  )
}
